import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { getErrorMessage } from '@billda/shared-ui';
import { useCreatePlot } from './usePlotMutations.ts';
import { addPlotSchema } from '../validation/addPlotSchema.ts';
import type { AddPlotFormData } from '../validation/addPlotSchema.ts';

interface UseAddPlotFormOptions {
  onSuccess?: () => void;
}

/**
 * Orchestration hook for Add Plot form
 * Follows the 9-section pattern from form-architecture.md
 */
export const useAddPlotForm = (
  siteId: number,
  options?: UseAddPlotFormOptions
) => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 1. Routing & URL Parameters
  // (No navigation needed for this form)

  // 2. Data Fetching
  // (No additional queries needed)

  // 3. Dynamic Yup Schema
  const validationSchema = addPlotSchema;

  // 4. React Hook Form Setup
  const form = useForm<AddPlotFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      plotNumber: '',
    },
    mode: 'onBlur', // Validate on blur for better UX
  });

  const { reset } = form;

  // 5. Watch Form Values
  // (No watched values needed)

  // 6. Auto-Selection Logic
  // (No auto-fill logic needed)

  // 7. Submission Handling
  const mutation = useCreatePlot(siteId);

  const handleSubmit = async (data: AddPlotFormData) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    const request = {
      name: data.name.trim(),
      plotNumber: data.plotNumber.trim(),
    };

    try {
      const result = await mutation.mutateAsync(request);
      const message = `Plot "${result.name}" (${result.plotNumber}) created successfully`;
      setSuccessMessage(message);
      setErrorMessage(null);

      // Reset form
      reset();

      // Call success callback if provided
      options?.onSuccess?.();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
      setSuccessMessage(null);
    }
  };

  // 8. Navigation Handlers
  const handleCancel = () => {
    reset();
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  // 9. Return Hook Interface
  return {
    form,
    handleSubmit: form.handleSubmit(handleSubmit),
    submitData: handleSubmit, // Exposed for manual submission (e.g., confirmation dialogs)
    handleCancel,
    isSubmitting: mutation.isPending,
    successMessage,
    errorMessage,
    clearMessages: () => {
      setSuccessMessage(null);
      setErrorMessage(null);
    },
  };
};
